/**
 * 008-reporting-dashboards T015/T016 (FR-008, FR-011, SC-003). Column sets for `Variance.tsx`'s
 * five breakdowns, one `ColumnDef` array per table — same shape as `stock-movement-columns.tsx`.
 *
 * Accessors so a header click sorts the whole fetched breakdown (each figure on the API's own value
 * read as a number, shown as sent); figures right-align through `meta.align`. The trend table is the
 * exception: its order is the weeks' own, so it does not sort.
 */
import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { getStatusBadge } from "@steelyard/theme";
import { formatDate, formatMoment } from "../../lib/format";
import {
  formatPercent,
  personName,
  reasonCodeLabel,
  type PackerVarianceRow,
  type SkuVarianceRow,
  type ReasonCodeRow,
  type VarianceTrendPoint,
  type VarianceEventRow,
} from "../../lib/services/warehouse";
import { inventoryPath } from "../../lib/paths";

/** A right-aligned figure column: sorts on the API's own value read as a number, shows it as sent. */
function figure<T>(id: keyof T & string, header: string, show: (row: T) => ReactNode = (row) => String(row[id])): ColumnDef<T> {
  return { id, accessorFn: (row) => Number(row[id]), header, meta: { align: "right" }, cell: ({ row }) => show(row.original) };
}

/** The packer by the API's own `packerName`; one it could not resolve reads "Unknown user", never the id. */
export const packerVarianceColumns: ColumnDef<PackerVarianceRow>[] = [
  { id: "packerId", accessorFn: (row) => personName(row.packerName), header: "Packer", cell: ({ row }) => personName(row.original.packerName) },
  figure<PackerVarianceRow>("linesPacked", "Lines packed"),
  figure<PackerVarianceRow>("amberCount", "Amber"),
  figure<PackerVarianceRow>("redCount", "Red"),
  figure<PackerVarianceRow>("varianceRate", "Variance rate", (row) => formatPercent(row.varianceRate)),
  figure<PackerVarianceRow>("collisionConfirmations", "Collision confirmations"),
  figure<PackerVarianceRow>("excludedLines", "Excluded lines"),
];

export const skuVarianceColumns: ColumnDef<SkuVarianceRow>[] = [
  {
    id: "skuName",
    accessorKey: "skuName",
    header: "SKU",
    cell: ({ row }) => (
      <Link to={inventoryPath(row.original.skuId)} className="hover:underline">
        {row.original.skuName}
      </Link>
    ),
  },
  figure<SkuVarianceRow>("linesPacked", "Lines packed"),
  figure<SkuVarianceRow>("amberCount", "Amber"),
  figure<SkuVarianceRow>("redCount", "Red"),
  figure<SkuVarianceRow>("varianceRate", "Variance rate", (row) => formatPercent(row.varianceRate)),
];

export const reasonCodeVarianceColumns: ColumnDef<ReasonCodeRow>[] = [
  { id: "reasonCode", accessorFn: (row) => (row.reasonCode ? reasonCodeLabel(row.reasonCode) : "—"), header: "Reason", cell: ({ row }) => (row.original.reasonCode ? reasonCodeLabel(row.original.reasonCode) : "—") },
  figure<ReasonCodeRow>("count", "Count"),
];

/**
 * FR-011: "a period column and a delta" read against the same contract's own "no percentage
 * change between periods" prohibition (spec's computation list; `tasks.md` T030 greps for
 * subtraction/division over report rows) — a numeric delta is exactly what that forbids. Direction
 * is a plain `>`/`<` comparison of two already-API-supplied `varianceRate` values, never stored or
 * exported: legible "which way" with no client-computed figure.
 */
function direction(current: VarianceTrendPoint, previous: VarianceTrendPoint | undefined): string {
  if (!previous || current.varianceRate === null || previous.varianceRate === null) return "—";
  const currentRate = Number(current.varianceRate);
  const previousRate = Number(previous.varianceRate);
  if (currentRate > previousRate) return "↑ Rising";
  if (currentRate < previousRate) return "↓ Falling";
  return "→ Flat";
}

export function trendColumns(rows: VarianceTrendPoint[]): ColumnDef<VarianceTrendPoint>[] {
  return [
    { id: "weekStarting", header: "Week starting", cell: ({ row }) => formatDate(row.original.weekStarting) },
    { id: "linesPacked", header: "Lines packed", meta: { align: "right" }, cell: ({ row }) => row.original.linesPacked },
    { id: "eventCount", header: "Events", meta: { align: "right" }, cell: ({ row }) => row.original.eventCount },
    { id: "varianceRate", header: "Variance rate", meta: { align: "right" }, cell: ({ row }) => formatPercent(row.original.varianceRate) },
    {
      id: "direction",
      header: "Direction",
      cell: ({ row }) => {
        const index = rows.indexOf(row.original);
        return direction(row.original, index > 0 ? rows[index - 1] : undefined);
      },
    },
  ];
}

/** People and the order by the API's own labels (`packerName`, `overrideByName`, `orderNumber`). */
export const varianceEventColumns: ColumnDef<VarianceEventRow>[] = [
  // ISO timestamps — sort chronologically as text.
  { id: "capturedAt", accessorKey: "capturedAt", header: "Captured", cell: ({ row }) => formatMoment(row.original.capturedAt) },
  { id: "syncedAt", accessorKey: "syncedAt", header: "Synced", cell: ({ row }) => formatMoment(row.original.syncedAt) },
  { id: "packerId", accessorFn: (row) => personName(row.packerName), header: "Packer", cell: ({ row }) => personName(row.original.packerName) },
  { id: "order", accessorFn: (row) => row.orderNumber ?? "", header: "Order", cell: ({ row }) => row.original.orderNumber ?? "—" },
  {
    id: "sku",
    accessorKey: "skuName",
    header: "SKU",
    cell: ({ row }) => (
      <Link to={inventoryPath(row.original.skuId)} className="hover:underline">
        {row.original.skuName}
      </Link>
    ),
  },
  { id: "band", accessorKey: "band", header: "Band", cell: ({ row }) => getStatusBadge(row.original.band) },
  { id: "reasonCode", accessorFn: (row) => (row.reasonCode ? reasonCodeLabel(row.reasonCode) : ""), header: "Reason", cell: ({ row }) => (row.original.reasonCode ? reasonCodeLabel(row.original.reasonCode) : "—") },
  { id: "reasonNote", accessorFn: (row) => row.reasonNote ?? "", header: "Note", cell: ({ row }) => row.original.reasonNote ?? "—" },
  {
    id: "override",
    accessorFn: (row) => (row.overrideBy ? personName(row.overrideByName) : ""),
    header: "Override",
    cell: ({ row }) =>
      row.original.overrideBy ? (
        <div className="flex flex-col">
          <span>{personName(row.original.overrideByName)}</span>
          <span className="text-muted-foreground text-xs">{row.original.overrideReason}</span>
        </div>
      ) : (
        "—"
      ),
  },
];
